import React from 'react';
import { IconType } from 'react-icons';
import { cn } from '../../lib/utils';

interface StatCardProps {
  className?: string;
  label: string;
  value: string;
  change: string;
  weeklyChange: string;
  dailyChange: string;
  color: string;
  icon: IconType;
  link: string;
  totalToday?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  className,
  label,
  value,
  change,
  weeklyChange,
  dailyChange,
  color,
  icon: Icon,
  totalToday,
}) => {
  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const getColorClasses = () => {
    switch (color) {
      case 'blue':
        return {
          iconBg: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30',
          iconText: 'text-blue-600 dark:text-blue-400',
          valueGradient: 'from-blue-600 via-blue-500 to-blue-400',
          borderHover: 'hover:border-blue-300 dark:hover:border-blue-600',
          labelHover: 'group-hover:text-blue-600 dark:group-hover:text-blue-400'
        };
      case 'green':
        return {
          iconBg: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30',
          iconText: 'text-green-600 dark:text-green-400',
          valueGradient: 'from-green-600 via-green-500 to-green-400',
          borderHover: 'hover:border-green-300 dark:hover:border-green-600',
          labelHover: 'group-hover:text-green-600 dark:group-hover:text-green-400'
        };
      case 'purple':
        return {
          iconBg: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30',
          iconText: 'text-purple-600 dark:text-purple-400',
          valueGradient: 'from-purple-600 via-purple-500 to-purple-400',
          borderHover: 'hover:border-purple-300 dark:hover:border-purple-600',
          labelHover: 'group-hover:text-purple-600 dark:group-hover:text-purple-400'
        };
      case 'indigo':
        return {
          iconBg: 'bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30',
          iconText: 'text-indigo-600 dark:text-indigo-400',
          valueGradient: 'from-indigo-600 via-indigo-500 to-indigo-400',
          borderHover: 'hover:border-indigo-300 dark:hover:border-indigo-600',
          labelHover: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400'
        };
      case 'orange':
        return {
          iconBg: 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30',
          iconText: 'text-orange-600 dark:text-orange-400',
          valueGradient: 'from-orange-600 via-orange-500 to-orange-400',
          borderHover: 'hover:border-orange-300 dark:hover:border-orange-600',
          labelHover: 'group-hover:text-orange-600 dark:group-hover:text-orange-400'
        };
      default:
        return {
          iconBg: 'bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/30',
          iconText: 'text-teal-600 dark:text-teal-400',
          valueGradient: 'from-teal-600 via-teal-500 to-teal-400',
          borderHover: 'hover:border-teal-300 dark:hover:border-teal-600',
          labelHover: 'group-hover:text-teal-600 dark:group-hover:text-teal-400'
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div
      className={cn(
        `relative glass-strong rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-4 border ${colors.borderHover} group cursor-pointer overflow-hidden transform hover:-translate-y-0.5 animate-fade-in h-full flex flex-col`,
        className
      )}
    >
      {/* Animated gradient background */}
      <div className={`absolute -top-16 -right-16 w-28 h-28 ${colors.iconBg} rounded-full blur-2xl opacity-25 group-hover:opacity-40 transition-all duration-500`}></div>
      <div className={`absolute -bottom-8 -left-8 w-24 h-24 ${colors.iconBg} rounded-full blur-xl opacity-20 group-hover:opacity-30 transition-all duration-500`}></div>
      
      {/* Shimmer effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="absolute inset-0 shimmer"></div>
      </div>
      
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors.iconBg} group-hover:scale-105 transition-all duration-300 shadow-sm relative overflow-hidden`}>
            <div className={`absolute inset-0 ${colors.iconBg} opacity-50 group-hover:opacity-100 transition-opacity duration-500`}></div>
            <div className="relative z-10">
              {renderIcon(Icon, `w-5 h-5 ${colors.iconText} group-hover:scale-105 transition-transform duration-300`)}
            </div>
          </div>
        </div>
        
        <h3 className={`text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 ${colors.labelHover} transition-colors duration-300 uppercase tracking-wide`}>
          {label}
        </h3>
        
        <div className="mb-3 flex items-baseline gap-2 flex-wrap">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Toplam</span>
            <p className={`text-3xl font-extrabold bg-gradient-to-r ${colors.valueGradient} bg-clip-text text-transparent`}>
              {value}
            </p>
          </div>
          {totalToday && (
            <>
              <span className="text-lg font-bold text-slate-300 dark:text-slate-600">|</span>
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Bugün</span>
                <span className={`text-3xl font-extrabold ${colors.iconText}`}>
                  {totalToday}
                </span>
              </div>
            </>
          )}
        </div>
        
        <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-200/50 dark:border-gray-700/50 mt-auto">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-md shadow-sm ${
              dailyChange.startsWith('+') || dailyChange.startsWith('0')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {dailyChange}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-md shadow-sm ${
              weeklyChange.startsWith('+') || weeklyChange.startsWith('0')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {weeklyChange}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-md shadow-sm ${
              change.startsWith('+') || change.startsWith('0')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {change}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatCard;

